import React, { useMemo } from 'react';
import { Header, Button, Message, Icon, Table, Form, Dropdown, Accordion } from 'semantic-ui-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { LoadingWrapper } from '../common';
import { IcctDatePicker } from '../common/IcctDatePicker';
import { useAssetProblemReport, useBreakdownAccordion } from './hooks';
import { problemTypeLabel, problemTypeChartColor } from './constants';
import { TeamProblemsPanel } from './TeamProblemsPanel';
import './AssetProblemReport.css';

const formatDays = (val, decimals = 1) => {
    if (val === null || val === undefined) return '—';
    return `${parseFloat(Number(val).toFixed(decimals))}d`;
};

// ─── SummaryTile ──────────────────────────────────────────────────────────────

const SummaryTile = ({ label, value, sub }) => (
    <div className="apr-summary-card">
        <div className="apr-summary-card__label">{label}</div>
        <div className="apr-summary-card__value">{value}</div>
        {sub && <div className="apr-summary-card__sub">{sub}</div>}
    </div>
);

// ─── TypeDonutChart ───────────────────────────────────────────────────────────

const TypeDonutChart = ({ byType }) => {
    const chartData = useMemo(() => (byType || []).map((r) => ({
        type: r.type,
        name: r.label || problemTypeLabel(r.type),
        value: r.count ?? 0,
        percent: r.percent,
    })), [byType]);

    const chartTotal = chartData.reduce((acc, d) => acc + d.value, 0);

    if (chartData.length === 0) {
        return <p className="apr-empty-text">No problem-type data for the selected filters.</p>;
    }

    return (
        <div>
            <div className="apr-card-title">Problems by Type</div>
            <div className="apr-chart-flex">
                <div className="apr-donut-wrap">
                    <PieChart
                        key={chartData.map((d) => `${d.type}:${d.value}`).join('|')}
                        width={180}
                        height={180}
                    >
                        <Pie
                            data={chartData}
                            cx={85}
                            cy={85}
                            innerRadius={55}
                            outerRadius={80}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                            paddingAngle={2}
                        >
                            {chartData.map((entry) => (
                                <Cell key={entry.type} fill={problemTypeChartColor(entry.type)} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value, name, entry) => [`${value} (${entry?.payload?.percent ?? (chartTotal > 0 ? Math.round((value / chartTotal) * 100) : 0)}%)`, name]}
                        />
                    </PieChart>
                    <div className="apr-donut-center">
                        <div className="apr-donut-center__value">{chartTotal}</div>
                        <div className="apr-donut-center__label">Total</div>
                    </div>
                </div>
                <div className="apr-legend">
                    {chartData.map((entry) => {
                        const pct = entry.percent ?? (chartTotal > 0 ? Math.round((entry.value / chartTotal) * 100) : 0);
                        return (
                            <div key={entry.type} className="apr-legend__row">
                                <span className="apr-legend__swatch" style={{ background: problemTypeChartColor(entry.type) }} />
                                <span>{entry.name} – {entry.value} ({pct}%)</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ─── AssetTypeBarChart ─────────────────────────────────────────────────────────

const reshapeByAssetType = (byAssetType) => (byAssetType || []).map((row) => {
    const shaped = { assetType: row.assetType };
    (row.byProblemType || []).forEach((pt) => {
        shaped[pt.type] = pt.count ?? 0;
    });
    return shaped;
});

const AssetTypeBarChart = ({ byAssetType }) => {
    const chartData = useMemo(() => reshapeByAssetType(byAssetType), [byAssetType]);

    // Problem types are free text (not a fixed enum), so bar keys must come from the data itself.
    const types = useMemo(() => {
        const set = new Set();
        (byAssetType || []).forEach((row) => (row.byProblemType || []).forEach((pt) => set.add(pt.type)));
        return Array.from(set);
    }, [byAssetType]);

    if (chartData.length === 0) {
        return <p className="apr-empty-text">No asset-type data for the selected filters.</p>;
    }

    return (
        <div>
            <div className="apr-card-title">Problems by Asset Type</div>
            <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 70, left: 0 }}>
                    <XAxis
                        dataKey="assetType"
                        interval={0}
                        angle={-35}
                        textAnchor="end"
                        height={90}
                        tick={{ fontSize: 12 }}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    {types.map((type) => (
                        <Bar key={type} dataKey={type} stackId="assetType" fill={problemTypeChartColor(type)} name={problemTypeLabel(type)} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

// ─── AvgResolutionByTypeTable ───────────────────────────────────────────────────

const AvgResolutionByTypeTable = ({ byType, avgDaysOverall }) => (
    <div>
        <div className="apr-section-header">
            <span className="apr-card-title">Average Time to Resolve</span>
            {avgDaysOverall != null && (
                <span className="apr-section-header__sub">
                    Overall Avg.&nbsp;<strong>{formatDays(avgDaysOverall)}</strong>
                </span>
            )}
        </div>
        <Table basic="very" compact unstackable className="apr-table">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Avg. Days</Table.HeaderCell>
                    <Table.HeaderCell>VS Overall</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {(byType || []).map((row) => {
                    const days = row.avgDays ?? null;
                    const diff = days != null && avgDaysOverall != null ? days - avgDaysOverall : null;
                    return (
                        <Table.Row key={row.type}>
                            <Table.Cell>{row.label || problemTypeLabel(row.type)}</Table.Cell>
                            <Table.Cell>{formatDays(days)}</Table.Cell>
                            <Table.Cell>
                                {diff === null ? '—' : (
                                    <span className={diff > 0 ? 'apr-diff apr-diff--worse' : 'apr-diff apr-diff--better'}>
                                        {diff > 0 ? '+' : ''}{diff.toFixed(1)}d
                                    </span>
                                )}
                            </Table.Cell>
                        </Table.Row>
                    );
                })}
            </Table.Body>
        </Table>
    </div>
);

// ─── BreakdownTable (leaf-level rows, e.g. teams within a market) ───────────────

const BreakdownTable = ({ rows, onRowClick, isRowExpanded, renderExpanded }) => (
    <div className="apr-breakdown-table">
        <Table basic="very" compact unstackable className="apr-table">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Name</Table.HeaderCell>
                    <Table.HeaderCell>Open</Table.HeaderCell>
                    <Table.HeaderCell>Resolved</Table.HeaderCell>
                    <Table.HeaderCell>Days Impacted</Table.HeaderCell>
                    <Table.HeaderCell>Avg. Resolution</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {rows.map((row) => {
                    const isExpanded = isRowExpanded?.(row.key) ?? false;
                    return (
                        <React.Fragment key={row.key}>
                            <Table.Row
                                data-testid={`apr-group-row-${row.key}`}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                className={[
                                    onRowClick ? 'apr-row-clickable' : '',
                                    isExpanded ? 'apr-row-selected' : '',
                                ].filter(Boolean).join(' ')}
                            >
                                <Table.Cell className="apr-breakdown-table__name">
                                    {row.label}
                                    {onRowClick && <Icon name={isExpanded ? 'chevron down' : 'chevron right'} className="apr-row-icon" />}
                                </Table.Cell>
                                <Table.Cell>{row.metrics?.openCount ?? '—'}</Table.Cell>
                                <Table.Cell>{row.metrics?.resolvedCountInRange ?? '—'}</Table.Cell>
                                <Table.Cell>{row.metrics?.daysImpactedInRange ?? '—'}</Table.Cell>
                                <Table.Cell>{formatDays(row.metrics?.avgResolutionDaysOverall ?? null)}</Table.Cell>
                            </Table.Row>
                            {isExpanded && renderExpanded && (
                                <Table.Row className="apr-row-expanded">
                                    <Table.Cell colSpan={5} className="apr-row-expanded__cell">
                                        {renderExpanded(row)}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </React.Fragment>
                    );
                })}
            </Table.Body>
        </Table>
    </div>
);

// ─── BreakdownAccordion (State → Market → Team, each level fetched on expand) ───

const BreakdownAccordion = ({ accordion, startDate, endDate }) => {
    const {
        stateGroups, openStates, openMarkets, expandedTeams, marketsByState, teamsByMarket, loadingKeys,
        toggleState, toggleMarket, toggleTeamExpanded, marketKey, visibleStateKeys, isMarketVisible, isTeamVisible,
    } = accordion;

    const visibleStateGroups = visibleStateKeys
        ? stateGroups.filter((s) => visibleStateKeys.has(s.key))
        : stateGroups;

    if (visibleStateGroups.length === 0) {
        return <p className="apr-empty-text">No data for the selected filters.</p>;
    }

    return (
        <Accordion styled fluid className="apr-accordion">
            {visibleStateGroups.map((stateRow) => {
                const visibleMarkets = (marketsByState[stateRow.key] || []).filter((m) => isMarketVisible(stateRow.key, m.key));
                return (
                    <React.Fragment key={stateRow.key}>
                        <Accordion.Title
                            active={openStates.has(stateRow.key)}
                            onClick={() => toggleState(stateRow.key)}
                            className="apr-accordion__title"
                            data-testid={`apr-state-title-${stateRow.key}`}
                        >
                            <Icon name="dropdown" />
                            {stateRow.label}
                        </Accordion.Title>
                        <Accordion.Content active={openStates.has(stateRow.key)}>
                            {loadingKeys.has(stateRow.key) && <p className="apr-empty-text">Loading markets…</p>}
                            {!loadingKeys.has(stateRow.key) && visibleMarkets.length === 0 && (
                                <p className="apr-empty-text">No markets for this state.</p>
                            )}
                            {!loadingKeys.has(stateRow.key) && visibleMarkets.length > 0 && (
                                <Accordion styled fluid className="apr-accordion apr-accordion--nested">
                                    {visibleMarkets.map((marketRow) => {
                                        const mKey = marketKey(stateRow.key, marketRow.key);
                                        const visibleTeams = (teamsByMarket[mKey] || []).filter((t) => isTeamVisible(stateRow.key, marketRow.key, t.key));
                                        return (
                                            <React.Fragment key={mKey}>
                                                <Accordion.Title
                                                    active={openMarkets.has(mKey)}
                                                    onClick={() => toggleMarket(stateRow.key, marketRow.key)}
                                                    className="apr-accordion__title"
                                                    data-testid={`apr-market-title-${mKey}`}
                                                >
                                                    <Icon name="dropdown" />
                                                    {marketRow.label}
                                                </Accordion.Title>
                                                <Accordion.Content active={openMarkets.has(mKey)}>
                                                    {loadingKeys.has(mKey) && <p className="apr-empty-text">Loading teams…</p>}
                                                    {!loadingKeys.has(mKey) && visibleTeams.length === 0 && (
                                                        <p className="apr-empty-text">No teams for this market.</p>
                                                    )}
                                                    {!loadingKeys.has(mKey) && visibleTeams.length > 0 && (
                                                        <BreakdownTable
                                                            rows={visibleTeams}
                                                            onRowClick={(row) => toggleTeamExpanded(row.key)}
                                                            isRowExpanded={(key) => expandedTeams.has(key)}
                                                            renderExpanded={(row) => (
                                                                <TeamProblemsPanel
                                                                    teamId={row.key}
                                                                    teamLabel={row.label}
                                                                    startDate={startDate}
                                                                    endDate={endDate}
                                                                    onClose={() => toggleTeamExpanded(row.key)}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                </Accordion.Content>
                                            </React.Fragment>
                                        );
                                    })}
                                </Accordion>
                            )}
                        </Accordion.Content>
                    </React.Fragment>
                );
            })}
        </Accordion>
    );
};

// ─── AssetProblemReport ─────────────────────────────────────────────────────────

export const AssetProblemReport = () => {
    const {
        overall,
        filters,
        loaded,
        error,
        exporting,
        selectedStates,
        selectedMarkets,
        selectedTeamIds,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        handleStatesChange,
        handleMarketsChange,
        handleTeamsChange,
        handleClearFilters,
        handleExport,
    } = useAssetProblemReport();

    const stateOptions = useMemo(() => (filters.states || []).map((code) => (
        { key: code, value: code, text: code }
    )), [filters.states]);

    const marketOptions = useMemo(() => (filters.markets || [])
        .filter((m) => selectedStates.length === 0 || selectedStates.includes(m.state))
        .map((m) => ({ key: m.id, value: m.id, text: m.label })), [filters.markets, selectedStates]);

    const teamOptions = useMemo(() => (filters.teams || [])
        .filter((t) => selectedMarkets.length === 0 || selectedMarkets.map(String).includes(String(t.marketId)))
        .map((t) => ({ key: t.id, value: t.id, text: t.name })), [filters.teams, selectedMarkets]);

    const accordion = useBreakdownAccordion(startDate, endDate, { selectedStates, selectedMarkets, selectedTeamIds, filters });

    const hasActiveFilters = selectedStates.length > 0 || selectedMarkets.length > 0 || selectedTeamIds.length > 0;

    const readinessImpact = overall?.readinessDaysAffected != null
        ? `${overall.readinessDaysAffected} days`
        : overall?.readinessHoursAffected != null
            ? `${overall.readinessHoursAffected} hrs`
            : '—';

    return (
        <div className="apr-report">
            <div className="apr-report-header">
                <Header as="h2" className="apr-report-header__title">
                    Asset Problem Reporting Dashboard
                </Header>
                <p className="apr-report-header__subtitle">
                    Overview of asset problem volume, type distribution, and resolution performance.
                </p>
                <div className="apr-report-export">
                    <Button
                        basic
                        icon
                        labelPosition="left"
                        onClick={handleExport}
                        loading={exporting}
                        disabled={exporting || !overall}
                    >
                        <Icon name="file outline" />
                        Export Data
                    </Button>
                </div>
            </div>

            {error && (
                <Message negative>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            <div className="apr-filter-bar">
                <Form className="apr-select-filters">
                    <Form.Field className="apr-select-field">
                        <label>States</label>
                        <Dropdown
                            fluid
                            multiple
                            search
                            selection
                            placeholder="All States"
                            value={selectedStates}
                            options={stateOptions}
                            onChange={(_, { value }) => handleStatesChange(value)}
                        />
                    </Form.Field>
                    <Form.Field className="apr-select-field">
                        <label>Markets</label>
                        <Dropdown
                            fluid
                            multiple
                            search
                            selection
                            placeholder="All Markets"
                            value={selectedMarkets}
                            options={marketOptions}
                            onChange={(_, { value }) => handleMarketsChange(value)}
                        />
                    </Form.Field>
                    <Form.Field className="apr-select-field">
                        <label>Teams</label>
                        <Dropdown
                            fluid
                            multiple
                            search
                            selection
                            placeholder="All Teams"
                            value={selectedTeamIds}
                            options={teamOptions}
                            onChange={(_, { value }) => handleTeamsChange(value)}
                        />
                    </Form.Field>
                    <Form.Field className="apr-filter-actions-field">
                        <Button basic size="small" type="button" onClick={handleClearFilters} disabled={!hasActiveFilters}>
                            Clear Filters
                        </Button>
                    </Form.Field>
                </Form>

                <Form className="apr-date-filters">
                    <Form.Field className="apr-date-field">
                        <label>Start Date</label>
                        <IcctDatePicker
                            keyName="start"
                            value={startDate}
                            maxDate={endDate || new Date()}
                            onChange={(_, value) => setStartDate(value)}
                        />
                    </Form.Field>
                    <Form.Field className="apr-date-field">
                        <label>End Date</label>
                        <IcctDatePicker
                            keyName="end"
                            value={endDate}
                            maxDate={new Date()}
                            onChange={(_, value) => setEndDate(value)}
                        />
                    </Form.Field>
                </Form>
            </div>

            <LoadingWrapper loading={!loaded}>
                {overall && (
                    <>
                        <div className="apr-summary-row">
                            <SummaryTile label="Open Problems" value={overall.openCount ?? 0} sub="Currently unresolved" />
                            <SummaryTile label="Resolved (Range)" value={overall.resolvedCountInRange ?? 0} sub="Resolved within selected dates" />
                            <SummaryTile label="Days Impacted" value={overall.daysImpactedInRange ?? 0} sub="Unique work days impacted" />
                            <SummaryTile label="Readiness Impact" value={readinessImpact} sub="Affected readiness" />
                            <SummaryTile label="Avg. Resolution" value={formatDays(overall.avgResolutionDaysOverall ?? null)} sub="Overall average" />
                        </div>

                        <div className="apr-middle-row">
                            {startDate && endDate && (
                                <div className="apr-card">
                                    <AvgResolutionByTypeTable
                                        byType={overall.avgResolutionDaysByType}
                                        avgDaysOverall={overall.avgResolutionDaysOverall ?? null}
                                    />
                                </div>
                            )}
                            <div className="apr-card">
                                <TypeDonutChart byType={overall.byType} />
                            </div>
                        </div>

                        <div className="apr-card apr-asset-type-card">
                            <AssetTypeBarChart byAssetType={overall.byAssetType} />
                        </div>

                        <div>
                            <Header as="h4" className="apr-breakdown-title">States / Markets / Teams</Header>
                            <BreakdownAccordion
                                accordion={accordion}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        </div>
                    </>
                )}
            </LoadingWrapper>
        </div>
    );
};
